import { config } from "aaa";
import * as Formsy from "formsy-react";
import * as IBAN from "iban";

export function localizedNumber(value: number | string): number {
    return typeof value === "number" ? value : Number(String(value).replace(config.CURRENCY_DECIMAL_SEPARATOR, "."));
}

export const isInteger = (value: string) => {
    return (value !== undefined) && (value.length === 0 || /^[0-9]+$/i.test(value));
};

export const isNumber = (value: string) => {
    return value !== undefined &&
        isNaN(localizedNumber(value)) === false &&
        value.length === value.trim().length;
};

// Taken from here https://tylermcginnis.com/validate-email-address-javascript/
export function isEmail(email: string) {
    var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
}

export function maxDecimal(numDigits: number) {
    return (value: string) => {
        const isNum = isNumber(value);
        if (isNum) {
            const decimal = String(localizedNumber(value)).split(".")[1];
            return decimal === undefined || decimal.length <= numDigits;
        }

        return false;
    };
}

export function isInRange(min: number, max: number) {
    return (value: string) => {
        if (value !== undefined) {
            const localized = localizedNumber(value);
            return localized >= min && localized <= max;
        }

        return false;
    };
}

export function isGreater(limit: number) {
    return (value: string) => {
        if (value !== undefined) {
            if (value === "") {
                return true;
            }
            const localized = localizedNumber(value);
            return localized > limit;
        }

        return false;
    };
}

export function isPin(value: string) {
    if (!isInteger(value)) {
        return false;
    }

    if (value.length > 4) {
        return false;
    }

    if (value.length === 4) {
        if (value[0] === value[1] && value[1] === value[2] && value[2] === value[3]) {
            return false;
        }

        // Check ascending/descending
        const ascending = (v: any, delta: number) =>
            (Number(v[0]) + delta) === Number(v[1]) &&
            (Number(v[1]) + delta) === Number(v[2]) &&
            (Number(v[2]) + delta) === Number(v[3]);

        if (ascending(value, 1) || ascending(value, -1)) {
            return false;
        }
    }

    return true;
}

/**
 * Validates that a value is exactly 15 digits
 */
export function is15Digits(value: string): boolean {
    return value !== undefined && /^[0-9]{15}$/.test(value);
}

/**
 * Validates a number using the Luhn algorithm (modulus 10)
 * Used for credit card numbers and similar identification numbers
 */
//https://de.wikipedia.org/wiki/Luhn-Algorithmus
export function isValidLuhn(value: string): boolean {
    if (!value || typeof value !== 'string') {
        return false;
    }

    const digits = value.replace(/\D/g, '');

    if (digits.length === 0) {
        return false;
    }

    let sum = 0;
    let isEven = false;

    for (let i = digits.length - 1; i >= 0; i--) {
        let digit = parseInt(digits[i], 10);

        if (isEven) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
        isEven = !isEven;
    }

    return sum % 10 === 0;
}

export function isMilesAndMoreCardNumber(value: string): boolean {
    return is15Digits(value) && isValidLuhn(value);
}

/**
 * Validates European phone number against accepted formats:
 * - International format with country code: +43 123 456789, +49 30 12345678, +33 1 23 45 67 89
 * - International format with 00 prefix: 0043 123 456789, 0049 30 12345678
 * - National format: 01 23 45 67 89, 030 12345678
 * - With various separators: spaces, dashes, dots, parentheses
 * 
 * European phone numbers typically:
 * - Start with + or 00 followed by 1-3 digit country code (e.g., +43, 0043, +49, 0049)
 * - Have 8-15 digits total (including country code)
 * - May include leading zeros in national format
 */
export function isValidPhoneNumber(value: string): boolean {
    if (!value || typeof value !== 'string') {
        return false;
    }

    // Remove all whitespace, dashes, dots, parentheses, and slashes for digit extraction
    const cleaned = value.replace(/[\s\-\.\(\)\/]/g, '');

    // Must contain only digits and optionally a leading + or 00 prefix
    if (!/^(\+|\d)\d+$/.test(cleaned)) {
        return false;
    }

    // Check if it starts with + or 00 (international prefix)
    // Note: 00 is the international dialing prefix used in Europe (equivalent to +)
    const isInternational = cleaned.startsWith('+') || cleaned.startsWith('00');

    // Extract digits only (remove + or 00 if present)
    let digitsOnly = cleaned;
    if (cleaned.startsWith('+')) {
        digitsOnly = cleaned.substring(1); // Remove +
    } else if (cleaned.startsWith('00')) {
        digitsOnly = cleaned.substring(2); // Remove 00
    }

    // European phone numbers: 8-15 digits total (country code + national number)
    // Country codes: 1-3 digits (most European countries use 1-2 digits)
    // National numbers: typically 7-12 digits
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
        return false;
    }

    // If international format (+ or 00), validate it's a European country code
    if (isInternational) {
        // European country codes (1-3 digits): 
        // 1 digit: +7 (Russia/Kazakhstan - partially in Europe)
        // 2 digits: +43 (Austria), +49 (Germany), +33 (France), +44 (UK), +39 (Italy), etc.
        // 3 digits: +351 (Portugal), +352 (Luxembourg), +353 (Ireland), etc.
        const countryCodeMatch = digitsOnly.match(/^(\d{1,3})/);
        if (!countryCodeMatch) {
            return false;
        }

        const countryCode = countryCodeMatch[1];
        const nationalNumber = digitsOnly.substring(countryCode.length);

        // National number should be at least 6 digits and at most 12 digits
        if (nationalNumber.length < 6 || nationalNumber.length > 12) {
            return false;
        }
    } else {
        // National format (without country code)
        // Should be 8-12 digits (typical European national number length)
        if (digitsOnly.length < 8 || digitsOnly.length > 12) {
            return false;
        }
    }

    return true;
}

let rulesAdded = false;

export function addRules() {
    if (!rulesAdded) {
        rulesAdded = true;

        Formsy.addValidationRule("isInRange", (values: any, value: any, param: any) => isInRange(param[0], param[1])(value));

        Formsy.addValidationRule("isPin", (values: any, value: any, param: any) => {
            // Check correct length
            if (!value || value.length !== 4) {
                return false;
            }

            return isPin(value);
        });

        Formsy.addValidationRule("isActivationCode", (values: any, value: any, param: any) => {
            // Check correct length
            if (!value
                || value.length < 6
                || value.length > 15
            ) {
                return false;
            }

            return /^[a-z0-9]+$/i.test(value);
        });

        Formsy.addValidationRule("isValidIBAN", (values: any, value: any, param: any[]): boolean => {
            return IBAN.isValid(value);
        });

        Formsy.addValidationRule("is15Digits", (values: any, value: any, param: any) => {
            return is15Digits(value);
        });

        Formsy.addValidationRule("isValidLuhn", (values: any, value: any, param: any) => {
            return isValidLuhn(value);
        });

        Formsy.addValidationRule("isMilesAndMoreCardNumber", (values: any, value: any, param: any) => {
            return isMilesAndMoreCardNumber(value);
        });

        Formsy.addValidationRule("isValidClientPhone", (values: any, value: any, param: any) => {
            return isValidPhoneNumber(value);
        });
    }
}



// WEBPACK FOOTER //
// ./src/util/formValidations.ts